import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type {
  ApiErrorShape, Paginated, StoryDetail, StoryInput, StoryListItem,
} from './types';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v1/admin';

/**
 * The access token lives in MEMORY ONLY. The refresh token is an HTTP-only
 * cookie the browser sends on its own. Nothing auth-related touches
 * localStorage — a token there ends up in any XSS (B.5).
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onSessionExpired(handler: () => void): void {
  onSessionLost = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // the refresh cookie
  timeout: 30_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * One refresh in flight at a time. Without this a page that fires six queries
 * on mount performs six rotations, and rotation-with-reuse-detection revokes
 * the whole family the moment two of them race — logging the user out for
 * doing nothing wrong.
 */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // Uses the shared instance, not a bare axios call: one baseURL, one set of
  // interceptors, and — importantly — one adapter, so mock mode does not fire
  // a doomed request at the real API on every page load.
  // The response interceptor skips /auth/refresh, so this cannot recurse.
  refreshing ??= api
    .post<{ accessToken: string }>('/auth/refresh')
    .then((res) => {
      accessToken = res.data.accessToken;
      return accessToken;
    })
    .catch(() => {
      accessToken = null;
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error: ApiErrorShape }>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const code = error.response?.data?.error?.code;

    if (
      error.response?.status === 401 &&
      code !== 'INVALID_CREDENTIALS' &&
      original &&
      !original._retried &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login')
    ) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) return api(original);
      onSessionLost?.();
    }

    return Promise.reject(error);
  },
);

/** Normalise any failure into the API's error shape, so callers branch on one thing. */
export function toApiError(error: unknown): ApiErrorShape {
  if (axios.isAxiosError(error)) {
    const payload = (error.response?.data as { error?: ApiErrorShape } | undefined)?.error;
    if (payload?.code) return payload;
    if (error.code === 'ECONNABORTED' || !error.response) {
      return { code: 'INTERNAL', message: '__NETWORK__' };
    }
  }
  return { code: 'INTERNAL', message: '__UNKNOWN__' };
}

export function fieldError(error: ApiErrorShape, field: string): string | undefined {
  return error.details?.find((d) => d.field === field)?.message;
}

// ── Stories ───────────────────────────────────────────────────────────────
// Thin wrappers over the same admin axios instance the services calls use, so
// story reads/writes share the auth, refresh and error handling.

export const listStories = async (params?: Record<string, unknown>): Promise<Paginated<StoryListItem>> =>
  (await api.get<Paginated<StoryListItem>>('/stories', { params })).data;

export const getStory = async (id: string): Promise<{ item: StoryDetail }> =>
  (await api.get<{ item: StoryDetail }>(`/stories/${id}`)).data;

export const createStory = async (input: StoryInput): Promise<{ item: StoryDetail }> =>
  (await api.post<{ item: StoryDetail }>('/stories', input)).data;

export const updateStory = async (
  id: string,
  input: StoryInput & { expectedUpdatedAt?: string },
): Promise<{ item: StoryDetail }> =>
  (await api.patch<{ item: StoryDetail }>(`/stories/${id}`, input)).data;

export const publishStory = async (id: string): Promise<{ item: StoryDetail }> =>
  (await api.post<{ item: StoryDetail }>(`/stories/${id}/publish`)).data;

export const unpublishStory = async (id: string): Promise<{ item: StoryDetail }> =>
  (await api.post<{ item: StoryDetail }>(`/stories/${id}/unpublish`)).data;

export const reorderStories = async (ids: string[]): Promise<{ items: StoryListItem[] }> =>
  (await api.post<{ items: StoryListItem[] }>('/stories/reorder', { ids })).data;

export const deleteStory = async (id: string): Promise<void> => {
  await api.delete(`/stories/${id}`);
};

export const restoreStory = async (id: string): Promise<{ item: StoryDetail }> =>
  (await api.post<{ item: StoryDetail }>(`/stories/${id}/restore`)).data;

export { refreshAccessToken };
